import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BadgeDollarSign,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  CreditCard,
  Filter,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Smartphone,
  Wallet,
  X,
} from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import { PaymentsService } from "../../requests/admin/PaymentsService";
import PaymentStatusBadge from "../../components/Payments/PaymentStatusBadge";
import PaymentActionMenu from "../../components/Payments/PaymentActionMenu";
import PaymentDetailsModal from "../../components/Payments/PaymentDetailsModal";
import CommerceEmptyState from "../../components/Commerce/CommerceEmptyState";
import ListPagination from "../../components/Commerce/ListPagination";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { formatCurrency } from "../../utils/formatCurrency";
import {
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  formatLabel,
} from "../../utils/paymentHelpers";
import { PURCHASE_TYPE_OPTIONS, SORT_ORDER_OPTIONS } from "../../utils/orderHelpers";

const DEFAULT_QUERY = {
  page: 1,
  perPage: 15,
  search: "",
  status: "",
  paymentMethod: "",
  type: "",
  purchaseType: "",
  sortOrder: "desc",
  dateFrom: "",
  dateTo: "",
};

const CustomerAvatar = ({ name }) => {
  const initials = name
    ? name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")
    : "?";
  const colors = ["bg-emerald-100 text-emerald-600", "bg-blue-100 text-blue-600", "bg-violet-100 text-violet-600", "bg-amber-100 text-amber-600"];
  return (
    <div className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${colors[(name?.charCodeAt(0) ?? 0) % colors.length]}`}>
      {initials}
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, icon }) => (
  <div className="relative w-full lg:w-auto">
    <div className="flex absolute left-3 top-1/2 items-center text-gray-400 -translate-y-1/2 pointer-events-none">{icon}</div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className="appearance-none pl-9 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white w-full lg:min-w-[152px] focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub, gradient, iconBg, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-5 shadow-sm"
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-40 pointer-events-none`} />
    <div className="relative z-10 flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-1.5">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="mt-1.5 text-xs text-gray-400">{sub}</p>}
      </div>
      <div className={`p-2.5 rounded-xl ${iconBg}`}><Icon className="w-5 h-5" /></div>
    </div>
  </motion.div>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded max-w-[100px]" /></td>
    ))}
  </tr>
);

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 450);
  const [showFilters, setShowFilters] = useState(false);
  const [queryState, setQueryState] = useState(DEFAULT_QUERY);
  const [viewPayment, setViewPayment] = useState(null);

  useEffect(() => {
    setQueryState((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    let ignore = false;
    const fetchPayments = async () => {
      setIsFetching(true);
      const result = await PaymentsService.getPayments({
        page: queryState.page,
        limit: queryState.perPage,
        per_page: queryState.perPage,
        sort_order: queryState.sortOrder,
        search: queryState.search || undefined,
        status: queryState.status || undefined,
        payment_method: queryState.paymentMethod || undefined,
        type: queryState.type || undefined,
        purchase_type: queryState.purchaseType || undefined,
        date_from: queryState.dateFrom || undefined,
        date_to: queryState.dateTo || undefined,
      });

      if (ignore) return;

      if (result.success && result.data?.status_code === "000" && !result.data?.in_error) {
        setPayments(result.data.data?.data || []);
        setPagination(result.data.data?.pagination || null);
      } else {
        toast.error(result.data?.reason || result.message || "Failed to load payments");
        setPayments([]);
      }
      setIsInitialLoading(false);
      setIsFetching(false);
    };

    fetchPayments();
    return () => { ignore = true; };
  }, [queryState, refreshKey]);

  const stats = useMemo(() => {
    const pageAmount = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const completed = payments.filter((p) => p.status === "completed").length;
    const pending = payments.filter((p) => ["initiated", "pending"].includes(p.status)).length;
    return { pageAmount, completed, pending };
  }, [payments]);

  const hasActiveFilters = Boolean(
    queryState.status || queryState.paymentMethod || queryState.type ||
    queryState.purchaseType || queryState.dateFrom || queryState.dateTo || queryState.search
  );

  const activeFilterCount = [queryState.status, queryState.paymentMethod, queryState.type, queryState.purchaseType, queryState.dateFrom, queryState.dateTo].filter(Boolean).length;

  const updateQuery = (patch) => setQueryState((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));
  const clearFilters = () => {
    setSearchInput("");
    setQueryState(DEFAULT_QUERY);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Payment code copied");
  };

  return (
    <div className="min-h-screen bg-gray-50/70">
      <Navbar page="Payments" />

      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 px-5 py-8 sm:px-8">
        <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm"><Wallet className="w-6 h-6 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-white sm:text-3xl">Payment Management</h1>
              <p className="mt-1 text-sm text-white/80">Track transactions and payment statuses</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={isFetching}
            className="inline-flex gap-2 items-center self-start px-4 py-2.5 text-sm font-medium text-emerald-700 bg-white rounded-xl shadow-sm hover:shadow-md disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard icon={Wallet} label="Total Payments" value={pagination?.total ?? "—"} sub="Across all pages" gradient="from-emerald-100 to-teal-50" iconBg="bg-emerald-100 text-emerald-600" delay={0} />
          <StatCard icon={BadgeDollarSign} label="Page Amount" value={formatCurrency(stats.pageAmount)} sub="Current page total" gradient="from-cyan-100 to-sky-50" iconBg="bg-cyan-100 text-cyan-600" delay={0.05} />
          <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} sub="On this page" gradient="from-green-100 to-emerald-50" iconBg="bg-green-100 text-green-600" delay={0.1} />
          <StatCard icon={Clock} label="Pending" value={stats.pending} sub="Initiated / pending" gradient="from-amber-100 to-yellow-50" iconBg="bg-amber-100 text-amber-600" delay={0.15} />
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-5 border-b border-gray-100">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search payments, order codes, customers, phone..."
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters((p) => !p)}
                className={`inline-flex gap-2 items-center px-4 py-2.5 text-sm font-medium rounded-xl border transition-colors ${
                  showFilters ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="px-1.5 py-0.5 text-xs font-bold bg-white/20 rounded-full">{activeFilterCount}</span>
                )}
              </button>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <div className="grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2 lg:grid-cols-4">
                    <FilterSelect label="Status" value={queryState.status} onChange={(v) => updateQuery({ status: v })} options={PAYMENT_STATUS_OPTIONS} icon={<Filter className="w-4 h-4" />} />
                    <FilterSelect label="Method" value={queryState.paymentMethod} onChange={(v) => updateQuery({ paymentMethod: v })} options={PAYMENT_METHOD_OPTIONS} icon={<CreditCard className="w-4 h-4" />} />
                    <FilterSelect label="Type" value={queryState.type} onChange={(v) => updateQuery({ type: v })} options={PAYMENT_TYPE_OPTIONS} icon={<Smartphone className="w-4 h-4" />} />
                    <FilterSelect label="Purchase Type" value={queryState.purchaseType} onChange={(v) => updateQuery({ purchaseType: v })} options={PURCHASE_TYPE_OPTIONS} icon={<Wallet className="w-4 h-4" />} />
                    <FilterSelect label="Sort" value={queryState.sortOrder} onChange={(v) => updateQuery({ sortOrder: v })} options={SORT_ORDER_OPTIONS} icon={<RefreshCw className="w-4 h-4" />} />
                    <input type="date" value={queryState.dateFrom} onChange={(e) => updateQuery({ dateFrom: e.target.value })} className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl" aria-label="Date from" />
                    <input type="date" value={queryState.dateTo} onChange={(e) => updateQuery({ dateTo: e.target.value })} className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl" aria-label="Date to" />
                    {hasActiveFilters && (
                      <button type="button" onClick={clearFilters} className="inline-flex gap-1.5 items-center px-3 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100">
                        <X className="w-4 h-4" /> Clear all
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="px-5 py-3 border-b border-gray-50 bg-gray-50/60">
            <p className="text-sm text-gray-500">{isFetching ? "Updating..." : `${pagination?.total ?? 0} payments found`}</p>
          </div>

          {isInitialLoading ? (
            <div className="overflow-x-auto">
              <table className="w-full whitespace-nowrap">
                <thead className="bg-gray-50/50"><tr>{["Payment", "Order", "Customer", "Amount", "Method", "Status", "Paid At", "Created", ""].map((h) => <th key={h} className="px-5 py-3 text-xs font-semibold text-left text-gray-500 uppercase">{h}</th>)}</tr></thead>
                <tbody>{Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}</tbody>
              </table>
            </div>
          ) : payments.length === 0 ? (
            <CommerceEmptyState
              title={hasActiveFilters ? "No payments match your filters" : "No payments yet"}
              description={hasActiveFilters ? "Try adjusting your search or filter criteria." : "Payment records will appear here after checkout."}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              onRefresh={() => setRefreshKey((k) => k + 1)}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead className="bg-gray-50/50 border-b border-gray-100">
                    <tr>
                      {["Payment Code", "Order Code", "Customer", "Amount", "Method", "Status", "Paid At", "Created", "Actions"].map((h) => (
                        <th key={h} className={`px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide ${h === "Actions" ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {payments.map((payment, idx) => (
                      <motion.tr
                        key={payment.payment_code}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="hover:bg-emerald-50/40 transition-colors"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs font-medium text-gray-800">{payment.payment_code}</span>
                            <button type="button" onClick={() => copyCode(payment.payment_code)} className="p-1 text-gray-300 hover:text-gray-500"><Copy className="w-3 h-3" /></button>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="font-mono text-xs text-gray-600">{payment.order_code}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <CustomerAvatar name={payment.user?.full_name} />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{payment.user?.full_name || "—"}</p>
                              <p className="text-xs text-gray-400">{payment.phone_number || payment.user?.phone_number}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-bold text-gray-900">{formatCurrency(payment.amount)}</p>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-xs">
                            <p className="font-medium text-gray-700">{formatLabel(payment.payment_method)}</p>
                            <p className="text-gray-400">{payment.network || "—"}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4"><PaymentStatusBadge status={payment.status} /></td>
                        <td className="px-5 py-4 text-sm text-gray-500">
                          {payment.paid_at ? moment(payment.paid_at).format("DD MMM YYYY, hh:mm A") : "—"}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500">{moment(payment.created_at).format("DD MMM YYYY")}</td>
                        <td className="px-5 py-4 text-right">
                          <PaymentActionMenu
                            paymentLink={payment.payment_link}
                            onViewDetails={() => setViewPayment(payment)}
                          />
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPagination
                pagination={pagination}
                isFetching={isFetching}
                perPage={queryState.perPage}
                onPageChange={(page) => setQueryState((prev) => ({ ...prev, page }))}
                onPerPageChange={(perPage) => updateQuery({ perPage, page: 1 })}
              />
            </>
          )}
        </motion.div>
      </div>

      <PaymentDetailsModal
        isOpen={!!viewPayment}
        paymentCode={viewPayment?.payment_code}
        initialPayment={viewPayment}
        onClose={() => setViewPayment(null)}
      />
    </div>
  );
};

export default Payments;
